/**
 * Multi-select tier controls for the criterion-grouped protocol table.
 */

export const PROTOCOL_TIERS = ["T1", "T2", "T3", "T4"] as const;

type ProtocolTierFilterProps = {
  selectedTiers: string[];
  visibleAtomCount: number;
  totalAtomCount: number;
  visibleCriterionCount: number;
  onChange: (tiers: string[]) => void;
};

/**
 * Resolve the next selection after a tier chip click.
 *
 * Clicking a tier from the All state isolates it. Additional clicks build a
 * multi-select; removing the final tier restores All.
 *
 * @param selectedTiers - Currently selected tiers
 * @param tier - Tier chip that was clicked
 * @returns Next selected tiers
 */
export function toggleTierSelection(
  selectedTiers: string[],
  tier: string,
): string[] {
  if (selectedTiers.length === PROTOCOL_TIERS.length) return [tier];
  if (!selectedTiers.includes(tier)) {
    return PROTOCOL_TIERS.filter(
      (candidate) => selectedTiers.includes(candidate) || candidate === tier,
    );
  }
  const remaining = selectedTiers.filter((candidate) => candidate !== tier);
  return remaining.length > 0 ? remaining : [...PROTOCOL_TIERS];
}

/**
 * Render tier chips and the current filtered-result count.
 *
 * @param props - Selected tiers, counts, and change callback
 * @returns Tier filter toolbar
 */
export function ProtocolTierFilter({
  selectedTiers,
  visibleAtomCount,
  totalAtomCount,
  visibleCriterionCount,
  onChange,
}: ProtocolTierFilterProps) {
  const allSelected = selectedTiers.length === PROTOCOL_TIERS.length;

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 bg-slate-50 px-3 py-2">
      <div
        className="flex flex-wrap items-center gap-1.5"
        aria-label="Filter protocol atoms by tier"
      >
        <span className="mr-1 text-xs font-medium text-slate-600">Tier</span>
        <button
          type="button"
          className={`rounded-full border px-2.5 py-1 text-xs ${
            allSelected
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-300 bg-white text-slate-700"
          }`}
          aria-pressed={allSelected}
          onClick={() => onChange([...PROTOCOL_TIERS])}
        >
          All
        </button>
        {PROTOCOL_TIERS.map((tier) => {
          const selected = !allSelected && selectedTiers.includes(tier);
          return (
            <button
              key={tier}
              type="button"
              className={`rounded-full border px-2.5 py-1 text-xs ${
                selected
                  ? "border-sky-700 bg-sky-700 text-white"
                  : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"
              }`}
              aria-pressed={selected}
              onClick={() => onChange(toggleTierSelection(selectedTiers, tier))}
            >
              {tier}
            </button>
          );
        })}
      </div>
      <span className="text-xs text-slate-500">
        {visibleAtomCount} of {totalAtomCount} atoms · {visibleCriterionCount}{" "}
        criteria
      </span>
    </div>
  );
}
